import 'core-js'

import React, { ReactElement, ReactNode } from 'react'
import { connect } from 'react-redux'
import { NextPage } from 'next'
import hybrid from '@mobiuspace/larkplayer-hybrid'
import App, { AppInitialProps, AppContext, AppProps } from 'next/app'
import I18nProvider from 'next-translate/I18nProvider'
import { getCookie, setCookie } from 'cookies-next'
import qs from 'qs'

import configs from '@/configs'
import i18n, { NEXT_LOCALE, musicRecapYearlySupportedLngs } from '@/i18n'
import { wrapper } from '@/store'
import {
  setAccountInfo,
  setIsHybrid,
  setLang,
  setVersionCode,
} from '@/store/global'
import sa from '@/utils/sa'
import '../styles/reset.css'

export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode
}

type AppPropsWithLayout = { lang: string; dispatch: any } & AppProps & {
    Component: NextPageWithLayout
  }

type AppStates = {
  isInit: boolean
}

class MyApp extends App<AppPropsWithLayout, AppInitialProps, AppStates> {
  public static getInitialProps = wrapper.getInitialAppProps(
    (store) => async (appContext: AppContext) => {
      const { router, ctx } = appContext

      const { lang: queryLang } = router.query
      const pathname = ctx.pathname || router.pathname
      // 判断是否为 music_recap_yearly 页面
      const isMusicRecapYearly = pathname?.includes('/music-recap/yearly2025')

      let lang = ''

      // 根据页面类型选择支持的语言列表
      const supportedLanguages = isMusicRecapYearly
        ? musicRecapYearlySupportedLngs
        : i18n.supportedLngs

      // 验证语言是否在支持列表中
      const isLanguageSupported = (langStr: string) => {
        return (
          i18n.supportedLngs.includes(langStr) ||
          (isMusicRecapYearly &&
            musicRecapYearlySupportedLngs.includes(langStr))
        )
      }

      // 语言推导规则：query > browser > default
      if (typeof window === 'undefined') {
        // 服务器端：query > browser > default
        // @ts-ignore
        const browserLang = ctx.req?.acceptsLanguages(...supportedLanguages)
        const cookiesLang = getCookie(NEXT_LOCALE, {
          req: ctx.req,
          res: ctx.res,
        }) as string

        if (queryLang) {
          const queryLangStr = queryLang as string
          if (isLanguageSupported(queryLangStr)) {
            lang = queryLangStr
          } else {
            lang = i18n.fallbackLng
          }
        } else if (browserLang) {
          // 优先使用浏览器语言
          lang = browserLang
        } else {
          lang = i18n.fallbackLng
        }
        setCookie(NEXT_LOCALE, lang, { req: ctx.req, res: ctx.res })
      } else {
        // 客户端：query > cookie > default
        const cookiesLang = getCookie(NEXT_LOCALE) as string

        if (queryLang) {
          // 优先使用 query 参数
          const queryLangStr = queryLang as string
          if (isLanguageSupported(queryLangStr)) {
            lang = queryLangStr
            // 只有当 query 参数存在且有效时，才更新 cookie
            setCookie(NEXT_LOCALE, lang)
          } else {
            lang = i18n.fallbackLng
            setCookie(NEXT_LOCALE, lang)
          }
        } else if (cookiesLang && isLanguageSupported(cookiesLang)) {
          // 如果没有 query 参数，使用 cookie
          lang = cookiesLang
        } else {
          // 都没有则使用默认语言
          lang = i18n.fallbackLng
          setCookie(NEXT_LOCALE, lang)
        }
      }
      store.dispatch(setLang(lang))

      return {
        pageProps: {
          // https://nextjs.org/docs/advanced-features/custom-app#caveats
          ...(await App.getInitialProps(appContext)).pageProps,
          // Some custom thing for all pages
          pathname: ctx.pathname,
        },
        lang,
      }
    }
  )

  async componentDidMount() {
    const { sensorDebug } = qs.parse(window.location.search, {
      ignoreQueryPrefix: true,
    })
    sa.init({
      // 通过 sensorDebug=true 参数切换成调试模式
      server_url:
        sensorDebug === 'true'
          ? configs.sensor.debug_server_url
          : configs.sensor.server_url,
      is_track_single_page: true, // 单页面配置，默认开启，若页面中有锚点设计，需要将该配置删除，否则触发锚点会多触发 $pageview 事件
      use_client_time: true,
      send_type: 'beacon',
      show_log: true,
      heatmap: {
        //是否开启点击图，default 表示开启，自动采集 $WebClick 事件，可以设置 'not_collect' 表示关闭。
        clickmap: 'default',
        //是否开启触达注意力图，not_collect 表示关闭，不会自动采集 $WebStay 事件，可以设置 'default' 表示开启。
        scroll_notice_map: 'default',
      },
    })

    try {
      this.init()
    } catch (error) {
      console.error('App---init error', error)
    }
  }

  init = async () => {
    const { dispatch } = this.props
    const isSupportHybrid = await hybrid.isSupportHybrid()
    dispatch(setIsHybrid(isSupportHybrid))

    if (isSupportHybrid) {
      await this.initInApp()
    }
  }

  initInApp = async () => {
    const { dispatch } = this.props
    try {
      const deviceId = await hybrid.getUDID()
      const region = await hybrid.getRegion()
      const versionCode = await hybrid.getThisVersionCode()
      dispatch(setAccountInfo({ deviceId, region }))
      dispatch(setVersionCode(versionCode))
      // 公共字段 device_id，random_id，region，distinct_id
      // sa.registerPage({
      //   device_id: deviceId,
      //   region,
      // })
    } catch (error) {
      console.error('App---initInApp error', error)
    }
  }

  // 动态注入语言包
  namespaces = () => {
    const { lang, pageProps } = this.props

    // 加载 common 命名空间，如果不存在则 fallback 到默认语言
    let commonTranslation = null
    try {
      commonTranslation = require(`../i18n/common/${lang}.json`)
    } catch (error) {
      try {
        commonTranslation = require(`../i18n/common/${i18n.fallbackLng}.json`)
      } catch (e) {
        console.error(`[i18n] Failed to load common translation for ${lang}`, e)
      }
    }

    const obj: any = {
      common: commonTranslation || {},
    }

    const ns = pageProps.ns

    if (ns) {
      // music_recap_yearly 使用子目录结构：music_recap_yearly/${lang}/index.json
      // 其他命名空间使用扁平结构：${ns}/${lang}.json
      if (ns === 'music_recap_yearly') {
        // music_recap_yearly 支持 50+ 种语言，需要特殊处理
        let targetLang = lang
        let translationFile = null

        // 尝试加载指定语言的文件
        try {
          translationFile = require(`../i18n/${ns}/${targetLang}/index.json`)
        } catch (error) {
          // 如果文件不存在，尝试 fallback 逻辑
          // 1. 尝试语言代码的主部分（如 'pt-BR' -> 'pt'）
          const langMain = targetLang.split('-')[0]
          if (
            langMain !== targetLang &&
            musicRecapYearlySupportedLngs.includes(langMain)
          ) {
            try {
              translationFile = require(`../i18n/${ns}/${langMain}/index.json`)
              targetLang = langMain
            } catch (e) {
              // 继续 fallback
            }
          }

          // 2. 如果还是找不到，fallback 到默认语言 'en'
          if (!translationFile) {
            try {
              translationFile = require(`../i18n/${ns}/en/index.json`)
              targetLang = 'en'
            } catch (e) {
              console.error(
                `[i18n] Failed to load ${ns} translation for ${lang}, fallback to en also failed`,
                e
              )
            }
          }
        }

        if (translationFile) {
          obj[ns] = translationFile
        }
      } else {
        // 其他命名空间保持原有逻辑，只支持基础语言列表
        try {
          obj[ns] = require(`../i18n/${ns}/${lang}.json`)
        } catch (error) {
          // 如果文件不存在，fallback 到默认语言
          try {
            obj[ns] = require(`../i18n/${ns}/${i18n.fallbackLng}.json`)
          } catch (e) {
            console.error(
              `[i18n] Failed to load ${ns} translation for ${lang}`,
              e
            )
          }
        }
      }
    }

    return obj
  }

  render() {
    const { Component, pageProps, lang } = this.props
    const namespaces = this.namespaces()

    // Use the layout defined at the page level, if available
    const getLayout = Component.getLayout ?? ((page) => page)

    return (
      <I18nProvider lang={lang} namespaces={namespaces}>
        {getLayout(<Component {...pageProps} />)}
      </I18nProvider>
    )
  }
}

export default wrapper.withRedux(connect()(MyApp))
